import { FileDocumentType } from '@nkr/enums/document-type';
import { IWatrRequest, IPaystub } from '@nkr/models/index';
import { buildHintsForDocument, FindHintSource, resolveVerifyKind } from './find-hints';
import { FindHint } from '../types';
import { isHourly } from '../../utils/employment-type.utils';

function sortedPaystubs(data: IWatrRequest): IPaystub[] {
    const list = Array.isArray(data.paystubs) ? [...data.paystubs] : [];

    return list.sort((a, b) => {
        const seq = (a.paystubSequence ?? 0) - (b.paystubSequence ?? 0);

        if (seq !== 0) {
            return seq;
        }

        return (a.id || 0) - (b.id || 0);
    });
}

function positiveAmounts(values: Array<number | undefined | null>): number[] {
    return values
        .map(value => Number(value) || 0)
        .filter(amount => amount > 0);
}

export function buildWatrSidebarFindHints(
    data: IWatrRequest,
    docType: FileDocumentType | number,
    lineItems?: any[] | null,
    fileIndex?: number
): FindHint[] {
    const hourly = isHourly(data.employmentType);
    const paystubs = sortedPaystubs(data);
    const kind = resolveVerifyKind(docType);
    const source: FindHintSource = {
        name: data.name,
        firstName: data.firstName,
        lastName: data.lastName,
        alias: data.alias || data.donorAlias,
        donorAlias: data.donorAlias,
        ssnDecrypted: data.ssnDecrypted,
        birthDate: data.birthDate || data.birthdate,
        birthdate: data.birthdate || data.birthDate,
        incomeIrsYear: data.incomeIrsYear,
        incomeIrsYearGross: data.incomeIrsYearGross,
        incomePayStubStartDate: data.incomePayStubStartDate,
        incomePayStubEndDate: data.incomePayStubEndDate,
        email: data.email
    };

    if (lineItems && lineItems.length > 0) {
        const total = lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);

        if (total > 0) {
            source.receiptAmount = total;
        }

        if (lineItems[0].expenseDate) {
            source.receiptDate = lineItems[0].expenseDate;
        }
    }

    if (kind === 'paystub') {
        const fileAmounts = positiveAmounts((lineItems || []).map(item => item.amount));
        const indexedStub = fileIndex != null && fileIndex >= 0 ? paystubs[fileIndex] : undefined;
        const indexedAmount = indexedStub ? positiveAmounts([indexedStub.grossAmount]) : [];
        const allStubAmounts = positiveAmounts(paystubs.map(stub => stub.grossAmount));

        if (fileAmounts.length === 1) {
            source.incomePayStubGross = fileAmounts[0];
        } else if (fileAmounts.length > 1) {
            source.incomePayStubGrosses = fileAmounts;
        } else if (indexedAmount.length === 1) {
            source.incomePayStubGross = indexedAmount[0];
        } else if (allStubAmounts.length > 1) {
            source.incomePayStubGrosses = allStubAmounts;
        } else if (allStubAmounts.length === 1) {
            source.incomePayStubGross = allStubAmounts[0];
        } else if (!hourly && data.incomePayStubGross) {
            source.incomePayStubGross = data.incomePayStubGross;
        }

        if (indexedStub) {
            if (indexedStub.startDate) {
                source.incomePayStubStartDate = indexedStub.startDate;
            }

            if (indexedStub.endDate) {
                source.incomePayStubEndDate = indexedStub.endDate;
            }
        } else if (lineItems && lineItems.length > 0) {
            if (lineItems[0].expenseDate) {
                source.incomePayStubStartDate = lineItems[0].expenseDate;
            }

            const endDate = lineItems[0].endDate || lineItems[lineItems.length - 1].expenseDate;

            if (endDate) {
                source.incomePayStubEndDate = endDate;
            }
        }
    } else if (!hourly || paystubs.length <= 1) {
        source.incomePayStubGross = data.incomePayStubGross;
    }

    return buildHintsForDocument(docType, source, { hourly });
}

export function resolveSidebarDocType(
    displayType: FileDocumentType,
    viewerDocs: Array<{ docType: number }> | undefined,
    currentIndex: number,
    fileIds: number[] | undefined
): FileDocumentType {
    if (viewerDocs && viewerDocs.length > 0) {
        const doc = viewerDocs[Math.max(0, Math.min(currentIndex, viewerDocs.length - 1))];

        if (doc) {
            return doc.docType as FileDocumentType;
        }
    }

    if (fileIds && fileIds.length > 0) {
        return FileDocumentType.Receipts;
    }

    return displayType;
}
